import { combineReducers } from 'redux';

import {
  createResourcesReducer,
  createRootLinkReducer,
  getResourceURI,
  mlRel,
} from 'medialoopster/rest';

import { idFromHref } from '../../../businessRules/services/idFromHref';
import { SearchQuery } from '../../../sections/Header/services/SearchQueryService';
import { AssetType } from '../../types/asset/baseTypes';
import { Asset } from '../../types/asset/unionTypes';
import {
  DESELECT_ALL_ENTRIES,
  DeselectAllEntries,
  FETCH_SEARCH_RESULT,
  FetchSearchResult,
  LOAD_SHOTS,
  LoadShots,
  MULTI_SELECT_ENTRY,
  MultiSelectEntry,
  RANGE_SELECT_ENTRIES,
  RangeSelectEntries,
  SEARCH_LOADED,
  SearchLoaded,
  SearchRootState,
  SelectedAssetsState,
  SET_SEARCH_RESULT_SELECTION,
  SetSearchResultSelection,
} from './types';

export const assetSearchURIReducer = (
  state = '/api/search/videoassets/',
  { type, payload }: FetchSearchResult,
): string => {
  switch (type) {
    case FETCH_SEARCH_RESULT:
      return payload.url;
    default:
      return state;
  }
};

export const searchQueryReducer = (
  state = {},
  { type, payload }: FetchSearchResult,
): SearchQuery => {
  switch (type) {
    case FETCH_SEARCH_RESULT:
      return payload.query;
    default:
      return state;
  }
};

export const shotSearchURIReducer = (
  state: string | null = null,
  { type, payload }: LoadShots,
): string | null => {
  switch (type) {
    case LOAD_SHOTS:
      return payload.url;
    default:
      return state;
  }
};

export const initialSelectedAssetsState: SelectedAssetsState = {
  assetIds: [],
  assetTypeName: null,
};

export const selectAssetReducer = (
  state = initialSelectedAssetsState,
  action:
    | MultiSelectEntry
    | RangeSelectEntries
    | DeselectAllEntries
    | FetchSearchResult
    | SetSearchResultSelection,
): SelectedAssetsState => {
  switch (action.type) {
    case MULTI_SELECT_ENTRY: {
      const assetIds = state.assetIds.slice();
      const { payload } = action as MultiSelectEntry;
      const indexOfId = state.assetIds.indexOf(payload.assetId);
      if (assetIds.length === 0) {
        // if there was no selection add the clicked asset to the selection
        assetIds.splice(0, 0, payload.assetId);
        const currentAssetId = idFromHref(payload.currentAssetState.href);
        // if an asset is loaded with the same type as the search list entries
        // and the loaded asset is not the clicked asset
        // and the loaded asset is in the displayed search list entries
        if (
          payload.assetTypeName === payload.currentAssetState.type &&
          payload.assetHref !== payload.currentAssetState.href &&
          payload.entries.some(
            (entry: Asset) => getResourceURI(entry) === payload.currentAssetState.href,
          )
        ) {
          // also add the loaded asset to the selection
          assetIds.splice(0, 0, currentAssetId);
        }
      } else if (indexOfId >= 0) {
        // if clicked asset was already in the selection remove it from the selection
        assetIds.splice(indexOfId, 1);
      } else {
        // otherewise add it to the selection
        assetIds.splice(0, 0, payload.assetId);
      }
      return {
        ...state,
        assetIds,
      };
    }
    case RANGE_SELECT_ENTRIES: {
      const { payload } = action as RangeSelectEntries;
      // reference entry for range selection is the currently loaded asset
      if (payload.assetTypeName !== payload.currentAssetState.type) {
        // clicked asset type not matching -> no range selection
        return state;
      }
      const assetIds = [];
      const currentAssetId = idFromHref(payload.currentAssetState.href);
      if (payload.assetId === currentAssetId) {
        assetIds.push(payload.assetId);
      } else {
        // collect all entries between click asset and currently loaded asset
        let foundInId = false;
        const inOutIds = [payload.assetId, currentAssetId];
        payload.entries.some((entry: Asset) => {
          if (foundInId) {
            assetIds.push(entry.id);
            if (inOutIds.includes(entry.id)) {
              return true; // we have all entries in selection
            }
          } else if (inOutIds.includes(entry.id)) {
            foundInId = true;
            assetIds.push(entry.id);
          }
          return false;
        });
      }
      return {
        ...state,
        assetIds,
      };
    }
    case DESELECT_ALL_ENTRIES:
      return {
        ...state,
        assetIds: [],
      };
    case SET_SEARCH_RESULT_SELECTION:
      return action.payload;
    default:
      return state;
  }
};

export const searchCategoryReducer = (
  state: AssetType = 'videoasset',
  { type, payload }: FetchSearchResult,
): AssetType => {
  switch (type) {
    case FETCH_SEARCH_RESULT:
      return payload.assetTypeName;
    default:
      return state;
  }
};

export const isRefreshingOrLoadingReducer = (
  state = false,
  { type }: FetchSearchResult | SearchLoaded,
): boolean => {
  switch (type) {
    case FETCH_SEARCH_RESULT:
      return true;
    case SEARCH_LOADED:
      return false;
    default:
      return state;
  }
};

export default combineReducers<SearchRootState>({
  isRefreshingOrLoading: isRefreshingOrLoadingReducer,
  assetSearchURI: assetSearchURIReducer,
  shotSearchURI: shotSearchURIReducer,
  selection: selectAssetReducer,
  resultCollections: combineReducers({
    videoasset: createResourcesReducer('videoassetsearchresult-collection'),
    imageasset: createResourcesReducer('imageassetsearchresult-collection'),
    audioasset: createResourcesReducer('audioassetsearchresult-collection'),
    shot: createResourcesReducer('shotsearchresult-collection'),
    collection: createResourcesReducer('collectionsearchresult-collection'),
  }),
  category: searchCategoryReducer,
  searchQuery: searchQueryReducer,
  deviceFilterCollectionLink: createRootLinkReducer(mlRel('search/devicefilters')),
  deviceFilterCollections: createResourcesReducer('devicefilter-collection'),
});
